import React, { FC, Fragment, useContext, useState } from 'react';
import BackdropLoader from 'components/shared/backdrop-loader/backdrop-loader.component';
import { useMutation, useQuery } from '@apollo/client/react';
import { flagInvoiceAsUnpaidTemplate, getInvoiceTemplate } from 'apollo/templates/invoices';
import { useParams } from 'react-router-dom';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { Alert, Box, Grid, Paper } from '@mui/material';
import InvoiceSummaryPanel from 'components/invoice-summary/invoice-summary-panel.component';
import InvoiceDetails from 'components/invoice-summary/invoice-details/invoice-details.component';
import InvoicePageTitle from 'components/invoice-summary/invoice-page-title/invoice-page-title.component';
import SendMailDialogComponent from 'components/invoices/send-mail-dialog/send-mail-dialog.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { useTranslation } from 'react-i18next';
import { ApolloError } from '@apollo/client/errors';
import { Invoice, InvoiceDelivery, InvoiceLineItem, InvoiceStatus, Type } from 'models/invoice';
import SetInvoiceAsPaidComponent from 'components/invoice-summary/set-invoice-as-paid-dialog/set-invoice-as-paid-dialog.component';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import SetSentDateComponent from 'components/invoices/set-send-date-dialog/set-send-date-dialog.component';
import { Order } from 'models/order';
import CreditInvoicesPanel from 'components/invoice-summary/credit-invoices-panel.component';
import DebitInvoicePanel from 'components/invoice-summary/debit-invoice-panel.component';
import CreditInvoiceDialog from 'components/invoice-summary/credit-invoice-dialog/credit-invoice-dialog';
import { CONSTANTS } from 'config/constants';
import { customerSettings } from 'utils/customerSettings';
import { permissions } from 'utils/permission';
import { currentUserVar } from 'apollo/reactive-variables/user';
import ErrorDialog from 'components/shared/error-dialog/error-dialog.component';
import CertifyDialog from 'components/shared/certify-dialog/certify-dialog.component';
import { certifyUnCertifyOrderTemplate } from 'apollo/templates/order';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { currentOrgVar } from 'apollo/reactive-variables/organization';

const InvoiceSummary: FC = () => {
    const { invoiceId } = useParams<{ invoiceId: string }>();
    const { t: translate } = useTranslation(['invoices']);
    const currentUser = currentUserVar();
    const currentOrg = currentOrgVar();
    const [open, setOpen] = useState(false);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [isPaidDateModalOpen, setIsPaidDateModalOpen] = useState(false);
    const [isSentDateDialogOpen, setIsSentDateDialogOpen] = useState(false);
    const [isCreditInvoiceDialogOpen, setIsCreditInvoiceDialogOpen] = useState(false);
    const [changesSaved, setChangesSaved] = useState(false);
    const [isMailSent, setIsMailSent] = useState(false);
    const [isRetryInvoiceComplete, setIsRetryInvoiceComplete] = useState(false);
    const [invoicePaidSnackBar, setInvoicePaidSnackBar] = useState(false);
    const [invoice, setInvoice] = useState<Invoice>({} as Invoice);
    const [creditInvoices, setCreditInvoices] = useState<Invoice[]>([]);
    const [lineItemWithOrderCount, setLineItemWithOrderCount] = useState(0);
    const [certifiedOrderCount, setCertifiedOrderCount] = useState(0);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [apolloError, setApolloError] = useState<ApolloError | null>(null);
    const [isPriceSpecOpen, setIsPriceSpecOpen] = useState(false);
    const { setAppToasterContent } = useContext(AppToasterContext);
    const { showBoundary } = useDenthubErrorBoundary();

    let enforcedInvoiceDelivery = customerSettings.getCustomerSetting(
        invoice?.mainClinic?.customerSettings || [],
        CONSTANTS.SETTINGS_NAMES.INVOICING_DELIVERY,
    );

    const labOverrideClinicForcedSetting = customerSettings.getCustomerSetting(
        currentOrg.customerSettings || [],
        CONSTANTS.SETTINGS_NAMES.ALLOW_LAB_INVOICE_OVERRIDE,
    );
    if (labOverrideClinicForcedSetting && enforcedInvoiceDelivery !== InvoiceDelivery.SCHEDULED_ELECTRONIC) {
        enforcedInvoiceDelivery = '';
    }

    const isCertificationEnabled = customerSettings.getCustomerSetting(
        invoice?.mainClinic?.customerSettings || [],
        CONSTANTS.SETTINGS_NAMES.CERTIFICATION,
    );

    const isCertificationReportEnabled = customerSettings.getCustomerSetting(
        invoice?.mainClinic?.customerSettings || [],
        CONSTANTS.SETTINGS_NAMES.CERTIFICATION_REPORT,
    );

    const isShowOrderCreatorEnabled = customerSettings.getCustomerSetting(
        invoice?.mainClinic?.customerSettings || [],
        CONSTANTS.SETTINGS_NAMES.SHOW_ORDER_CREATOR_IN_PER_ORDER_INVOICE,
    );

    const { loading } = useQuery(getInvoiceTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: { _id: invoiceId },
        onCompleted({ invoice }) {
            setInvoice(invoice);
            setCreditInvoices(invoice.creditInvoices);
            const itemWithOrder = invoice?.lineItems?.filter((item: InvoiceLineItem) => !!item.order) || [];
            setLineItemWithOrderCount(itemWithOrder.length);
            const certifiedOrders = itemWithOrder.filter((item: InvoiceLineItem) => (item.order as Order)?.isCertified);
            setCertifiedOrderCount(certifiedOrders.length);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [flagInvoiceAsUnpaid] = useMutation(flagInvoiceAsUnpaidTemplate, {
        onCompleted({ flagInvoiceAsUnpaid: unpaidInvoice }) {
            setInvoice(Object.assign({}, invoice, unpaidInvoice));
            setIsConfirmDialogOpen(false);
        },
        onError: (err: ApolloError) => {
            setAppToasterContent({ message: err.message, severity: 'error', isOpen: true });
            setIsConfirmDialogOpen(false);
        },
    });

    const [certifyUnCertifyOrder] = useMutation(certifyUnCertifyOrderTemplate, {
        onCompleted: () => {
            setInvoice({ ...invoice, order: { ...(invoice.order as Order), isCertified: true } });
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const openPaidDateModal = () => {
        setIsPaidDateModalOpen(true);
    };

    const openSendInvoiceDialog = () => {
        setOpen(true);
    };

    const openSetUnpaidModal = () => {
        setIsConfirmDialogOpen(true);
    };

    const openSetSendDateDialog = () => {
        setIsSentDateDialogOpen(true);
    };

    const openCreateCreditInvoiceDialog = () => {
        setIsCreditInvoiceDialogOpen(true);
    };

    const handleDialogClose = () => {
        setOpen(false);
    };

    const handleSentDateDialogClose = () => {
        setIsSentDateDialogOpen(false);
    };

    const handlePaidDateModalClose = () => {
        setIsPaidDateModalOpen(false);
    };

    const handleCreditInvoiceModalClose = (error?: ApolloError, creditInvoice?: Invoice) => {
        if (error) {
            setShowErrorDialog(true);
            setApolloError(error);
        } else if (creditInvoice) {
            const invoices = [...creditInvoices];
            invoices.push(creditInvoice);
            setCreditInvoices(invoices);
            setInvoice({ ...invoice, creditInvoices: invoices, isCredited: true });
        }
        setIsCreditInvoiceDialogOpen(false);
    };

    const handlePaymentDateSet = (error?: ApolloError, updatedInvoice?: Invoice) => {
        if (error) {
            setAppToasterContent({ message: error.message, severity: 'error', isOpen: true });
        } else {
            setInvoice(Object.assign({}, { ...invoice }, updatedInvoice));
            setInvoicePaidSnackBar(true);
        }
        setIsPaidDateModalOpen(false);
    };

    const handleSentDateSet = (error?: ApolloError, updatedInvoice?: Invoice) => {
        if (error) {
            setAppToasterContent({ message: error.message, severity: 'error', isOpen: true });
        } else {
            setInvoice(Object.assign({}, { ...invoice }, updatedInvoice));
        }
        setIsSentDateDialogOpen(false);
    };

    const handleSendMail = (error?: ApolloError, updatedInvoice?: Invoice) => {
        if (error) {
            setAppToasterContent({ message: error.message, severity: 'error', isOpen: true });
        } else {
            setInvoice(Object.assign({}, { ...invoice }, updatedInvoice));
            setIsMailSent(true);
        }
        setOpen(false);
    };

    const onConfirm = async () => {
        // call unpaid API here
        await flagInvoiceAsUnpaid({ variables: { invoiceId } });
    };

    const handleClose = () => {
        setIsConfirmDialogOpen(false);
    };

    const handleCertifyUnCertify = (isCertify: boolean) => {
        setChangesSaved(true);
        setCertifiedOrderCount(certifiedOrderCount + (isCertify ? 1 : -1));
    };

    const onRetryInvoiceCreationComplete = () => {
        setIsRetryInvoiceComplete(true);
    };

    const onConfirmCertify = async () => {
        await certifyUnCertifyOrder({
            variables: { orderId: (invoice.order as Order)._id },
        });
        setIsPriceSpecOpen(false);
    };

    return (
        <Fragment>
            {isMailSent ? <AlertSnackbar type="success" message={translate('invoices:MAIL_SENT')} /> : undefined}
            {invoicePaidSnackBar ? (
                <AlertSnackbar type="success" message={translate('invoices:SET_AS_PAID')} />
            ) : undefined}
            {isRetryInvoiceComplete ? (
                <AlertSnackbar type="success" message={translate('invoices:RECREATE_INVOICE_COMPLETE')} />
            ) : undefined}
            {changesSaved ? <AlertSnackbar type="success" message={translate('common:CHANGES_SAVED')} /> : undefined}
            {loading ? (
                <BackdropLoader isOpen={loading} />
            ) : (
                <Grid container spacing={1}>
                    <Grid item xs={12} lg={8} xl={9}>
                        <Paper>
                            <Box p={2}>
                                <InvoicePageTitle
                                    lineItemWithOrderCount={lineItemWithOrderCount}
                                    certifiedOrderCount={certifiedOrderCount}
                                    invoice={invoice}
                                    openPriceSpecDialog={() => setIsPriceSpecOpen(true)}
                                />
                                {invoice.customer ? (
                                    <InvoiceDetails
                                        handleCertifyUnCertify={handleCertifyUnCertify}
                                        invoiceDetails={invoice}
                                        isShowOrderCreatorEnabled={isShowOrderCreatorEnabled}
                                    />
                                ) : (
                                    <Alert severity="info">{translate('DOWNLOAD_PDF_FOR_MORE_DETAILS')}</Alert>
                                )}
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} lg={4} xl={3}>
                        <Paper variant="outlined" square>
                            <Box p={2}>
                                <InvoiceSummaryPanel
                                    openSendInvoiceDialog={openSendInvoiceDialog}
                                    openPaidDateModal={openPaidDateModal}
                                    openSetUnpaidModal={openSetUnpaidModal}
                                    openSetSendDateDialog={openSetSendDateDialog}
                                    openCreateCreditInvoiceDialog={openCreateCreditInvoiceDialog}
                                    invoice={invoice}
                                    isCertificationSettingEnabled={
                                        isCertificationEnabled && isCertificationReportEnabled
                                    }
                                    onRetryInvoiceCreationComplete={onRetryInvoiceCreationComplete}
                                />
                            </Box>
                        </Paper>
                        {permissions.isLabTechnician(currentUser.type) && invoice.status === InvoiceStatus.SCHEDULED ? (
                            <Box mt={2}>
                                <Alert severity="info">{translate('invoices:SCHEDULED_INVOICE_DESCRIPTION')}</Alert>
                            </Box>
                        ) : undefined}
                        {invoice.isCredited ? (
                            <Box mt={2}>
                                <Paper variant="outlined" square>
                                    <Box p={2}>
                                        <CreditInvoicesPanel creditInvoices={creditInvoices} />
                                    </Box>
                                </Paper>
                            </Box>
                        ) : undefined}
                        {invoice.type === Type.CREDIT && invoice.debitInvoice ? (
                            <Box mt={2}>
                                <Paper variant="outlined" square>
                                    <Box p={2}>
                                        <DebitInvoicePanel
                                            debitInvoiceId={invoice.debitInvoice?._id}
                                            totalAmount={invoice.debitInvoice?.total?.totalIncVat || 0}
                                            currency={invoice.debitInvoice?.total?.currency}
                                            debitInvoiceNumber={invoice.debitInvoice?.invoiceNumber}
                                        />
                                    </Box>
                                </Paper>
                            </Box>
                        ) : undefined}
                    </Grid>
                </Grid>
            )}
            <SendMailDialogComponent
                open={open}
                invoice={invoice}
                onDialogClose={handleDialogClose}
                onSendMail={handleSendMail}
            />
            <SetInvoiceAsPaidComponent
                open={isPaidDateModalOpen}
                invoiceId={invoice._id}
                invoiceSentDate={invoice.sentDate as Date}
                onDialogClose={handlePaidDateModalClose}
                onPaymentDateSet={handlePaymentDateSet}
            />
            <SetSentDateComponent
                open={isSentDateDialogOpen}
                invoiceId={invoice._id}
                onDialogClose={handleSentDateDialogClose}
                onSentDateSet={handleSentDateSet}
            />
            <ConfirmDialog
                open={isConfirmDialogOpen}
                handleConfirm={onConfirm}
                handleClose={handleClose}
                confirmBtnLabel={translate('common:YES')}
            />
            <CreditInvoiceDialog
                debitInvoice={invoice}
                clinicName={invoice.customer?.name}
                deliveryMethod={
                    enforcedInvoiceDelivery || (invoice.invoiceSettings?.invoiceDelivery as InvoiceDelivery)
                }
                paymentTerms={invoice.invoiceSettings?.paymentTerms || 0}
                total={invoice.total}
                open={isCreditInvoiceDialogOpen}
                onDialogClose={handleCreditInvoiceModalClose}
            />
            <ErrorDialog
                errorMessage={apolloError?.message || ''}
                extraInfo={(apolloError?.graphQLErrors[0]?.extensions?.exception as any)?.otherInfo || {}}
                open={showErrorDialog}
                onClose={() => {
                    setShowErrorDialog(false);
                }}
            />
            <CertifyDialog
                open={isPriceSpecOpen}
                handleConfirm={onConfirmCertify}
                currency={invoice.total?.currency}
                handleClose={() => setIsPriceSpecOpen(false)}
                showUserAndDate
                restorations={(invoice?.order as Order)?.restorations || []}
            />
        </Fragment>
    );
};

export default InvoiceSummary;
